import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from './i18n/LanguageContext';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const PER_PAGE = 60;

const AmazighTranslation = ({
  API,
  isDarkMode,
  showNotification,
  userToken,
  hasPaidPlan,
  setCurrentView,
  setInitialProfileTab
}) => {
  const { t } = useTranslation();
  // App-shell caches: dialect-keyed so each dialect renders its data instantly.
  const _readCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  // Default initial dialect is 'tachelhit' — read its caches synchronously
  const _initDialect = 'tachelhit';
  const _cachedSettings = _readCache(`amazighSettings_${_initDialect}`);
  const _cachedLetterCounts = _readCache(`amazighLetterCounts_${_initDialect}`);
  const _cachedActiveLetter = _readCache(`amazighActiveLetter_${_initDialect}`);

  // Dialect switching
  const [activeDialect, setActiveDialect] = useState(_initDialect);

  const DIALECTS = {
    tachelhit: {
      labelKey: 'amazigh_dialect_tachelhit',
      settingsUrl: '/amazigh-translation/settings',
    },
    central_atlas: {
      labelKey: 'amazigh_dialect_central_atlas',
      settingsUrl: '/amazigh-central/settings',
    }
  };

  // Page settings from admin
  const [settings, setSettings] = useState(_cachedSettings || {
    page_title: "Amazigh Dictionary",
    page_subtitle: "English to Tamazight (Tifinagh)",
    beta_badge_text: "Beta / Experimental",
    disclaimer_text: "This translation may not be fully accurate. Tamazight is a low-resource language and translations should be verified.",
    max_input_chars: 200,
    is_pro_only: false
  });

  // Browse state — initial words from cached letter (if any)
  const _cachedWords = _cachedActiveLetter ? _readCache(`amazighWords_${_initDialect}_${_cachedActiveLetter}`) : null;
  const [words, setWords] = useState(_cachedWords || []);
  const [letterCounts, setLetterCounts] = useState(_cachedLetterCounts?.counts || {});
  const [totalWords, setTotalWords] = useState(_cachedLetterCounts?.total || 0);
  const [activeLetter, setActiveLetter] = useState(_cachedActiveLetter || '');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalForLetter, setTotalForLetter] = useState(0);
  const [loadingWords, setLoadingWords] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef(null);

  // Config — start true if we have cached settings so the page renders chrome immediately
  const [configLoaded, setConfigLoaded] = useState(!!_cachedSettings);
  const [showProModal, setShowProModal] = useState(false);
  const audioRef = useRef(null);
  const letterBarRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // ─── Back to top scroll listener ──────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ─── Load settings when dialect changes ───────────────────────────────────
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const url = DIALECTS[activeDialect].settingsUrl;
        const res = await fetch(`${API}${url}`);
        if (res.ok) {
          const data = await res.json();
          setSettings(prev => {
            const merged = { ...prev, ...data };
            try { localStorage.setItem(`amazighSettings_${activeDialect}`, JSON.stringify(merged)); } catch {}
            return merged;
          });
        }
      } catch (e) {
        console.error("Error loading Amazigh settings:", e);
      }
      setConfigLoaded(true);
    };
    loadSettings();
  }, [API, activeDialect]);

  // ─── Load letter counts when dialect changes ─────────────────────────────
  useEffect(() => {
    const loadCounts = async () => {
      try {
        const res = await fetch(`${API}/amazigh/letter-counts?dialect=${activeDialect}`);
        if (res.ok) {
          const data = await res.json();
          setLetterCounts(data.counts || {});
          setTotalWords(data.total || 0);
          try { localStorage.setItem(`amazighLetterCounts_${activeDialect}`, JSON.stringify({ counts: data.counts || {}, total: data.total || 0 })); } catch {}
          // Auto-select first letter that has words
          const firstLetter = LETTERS.find(l => (data.counts || {})[l] > 0) || 'A';
          setActiveLetter(firstLetter);
          try { localStorage.setItem(`amazighActiveLetter_${activeDialect}`, JSON.stringify(firstLetter)); } catch {}
        }
      } catch (e) {
        console.error("Error loading letter counts:", e);
      }
    };
    if (configLoaded) loadCounts();
  }, [API, activeDialect, configLoaded]);

  // ─── Load words when letter or page changes ──────────────────────────────
  useEffect(() => {
    if (!activeLetter || isSearchMode) return;
    const loadWords = async () => {
      if (page === 1) setLoadingWords(true);
      else setLoadingMore(true);
      try {
        const res = await fetch(`${API}/amazigh/browse?dialect=${activeDialect}&letter=${activeLetter}&page=${page}&per_page=${PER_PAGE}`);
        if (res.ok) {
          const data = await res.json();
          if (page === 1) {
            const ws = data.words || [];
            setWords(ws);
            // Cache page 1 only — keeps localStorage size bounded
            try { localStorage.setItem(`amazighWords_${activeDialect}_${activeLetter}`, JSON.stringify(ws)); } catch {}
          } else {
            setWords(prev => [...prev, ...(data.words || [])]);
          }
          setTotalForLetter(data.total || 0);
          setHasMore(data.has_more || false);
        }
      } catch (e) {
        console.error("Error loading words:", e);
      } finally {
        setLoadingWords(false);
        setLoadingMore(false);
      }
    };
    loadWords();
  }, [API, activeDialect, activeLetter, page, isSearchMode]);

  // ─── Debounced search ─────────────────────────────────────────────────────
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!searchQuery || searchQuery.trim().length < 2) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchTotal(0);
      return;
    }

    setIsSearchMode(true);
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(`${API}/amazigh/search?dialect=${activeDialect}&q=${encodeURIComponent(searchQuery.trim())}&per_page=${PER_PAGE}`);
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.words || []);
          setSearchTotal(data.total || 0);
        }
      } catch (e) {
        console.error("Search error:", e);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery, API, activeDialect]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const switchDialect = (dialect) => {
    if (dialect === activeDialect) return;
    setActiveDialect(dialect);
    setWords([]);
    setSearchQuery('');
    setIsSearchMode(false);
    setSearchResults([]);
    setPage(1);
    setActiveLetter('');
  };

  const selectLetter = (letter) => {
    if (!letterCounts[letter]) return;
    setActiveLetter(letter);
    setPage(1);
    setWords([]);
    setSearchQuery('');
    setIsSearchMode(false);
    // Scroll letter into view on mobile
    if (letterBarRef.current) {
      const btn = letterBarRef.current.querySelector(`[data-letter="${letter}"]`);
      if (btn) btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  };

  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    setPage(prev => prev + 1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearchMode(false);
    setSearchResults([]);
  };

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      showNotification(t('amazigh_copied'), "success");
    } catch (error) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        showNotification(t('amazigh_copied'), "success");
      } catch (fallbackError) {
        showNotification(t('amazigh_copy_failed'), "error");
      }
      document.body.removeChild(textArea);
    }
  };

  const playAudio = (url) => {
    if (!url) return;
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.play().catch(() => showNotification(t('amazigh_audio_failed'), "warning"));
      audio.onended = () => { audioRef.current = null; };
    } catch (e) {
      showNotification(t('amazigh_audio_error'), "error");
    }
  };

  // Which words to display
  const displayWords = isSearchMode ? searchResults : words;

  // ─── Loading state ────────────────────────────────────────────────────────
  if (!configLoaded) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <svg className="w-8 h-8 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
      </div>
    );
  }

  // ─── Pro-only gate ────────────────────────────────────────────────────────
  if (settings.is_pro_only && !hasPaidPlan) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center p-8 max-w-md">
          <span className="text-6xl mb-4 block">ⵣ</span>
          <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {settings.page_title}
          </h2>
          <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('amazigh_pro_only_message')}
          </p>
          <button
            onClick={() => setShowProModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
          >
            {t('upgrade_to_pro')}
          </button>
        </div>

        {showProModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={() => setShowProModal(false)}>
            <div className={`rounded-2xl p-8 max-w-md w-full text-center shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
              <div className="text-6xl mb-4">&#x1F512;</div>
              <h3 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {t('premium_content')}
              </h3>
              <p className={`text-base mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('premium_content_message')}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowProModal(false)}
                  className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {t('maybe_later')}
                </button>
                <button
                  onClick={() => { setShowProModal(false); if (setInitialProfileTab) setInitialProfileTab('membership'); if (setCurrentView) setCurrentView('profile'); }}
                  className="flex-1 py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg"
                >
                  {t('upgrade_to_pro')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Main UI ──────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        .amazigh-browse { min-height: 100vh; display: flex; flex-direction: column; padding-bottom: 5rem; }
        @media (max-width: 1023px) { .amazigh-browse { padding-bottom: 6rem; } }
        .letter-bar::-webkit-scrollbar { display: none; }
        .letter-bar { -ms-overflow-style: none; scrollbar-width: none; -webkit-overflow-scrolling: touch; }
        .word-card { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .word-card:hover { transform: translateY(-2px); }
      `}</style>

      <div className={`amazigh-browse ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} p-3 md:p-6 lg:pb-6`}>
        <div className="max-w-6xl mx-auto w-full flex-1 flex flex-col min-h-0">

          {/* Beta Badge */}
          <div className="mb-2 md:mb-3 flex justify-center">
            <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold ${
              isDarkMode ? 'bg-amber-900/40 text-amber-300 border border-amber-700' : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('amazigh_beta_badge')}
            </span>
          </div>

          {/* Dialect Tabs */}
          <div className="flex justify-center gap-1.5 mb-2 md:gap-2 md:mb-3">
            {Object.entries(DIALECTS).map(([key, dialect]) => (
              <button
                key={key}
                onClick={() => switchDialect(key)}
                className={`px-3 py-1.5 text-xs md:px-4 md:py-2 md:text-sm rounded-xl font-semibold transition-all ${
                  activeDialect === key
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : isDarkMode
                      ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200 border border-gray-700'
                      : 'bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700 border border-gray-200'
                }`}
              >
                {t(dialect.labelKey)}
              </button>
            ))}
          </div>

          {/* Title + Word Count */}
          <div className="text-center mb-3 md:mb-4">
            <h1 className={`text-xl md:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('amazigh_page_title')}
            </h1>
            <p className={`mt-0.5 text-xs md:text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {t('amazigh_page_subtitle')}
            </p>
            {totalWords > 0 && (
              <p className={`mt-1 text-xs font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                {totalWords.toLocaleString()} {t('amazigh_words_available')}
              </p>
            )}
          </div>

          {/* Search Bar */}
          <div className={`rounded-xl border overflow-hidden mb-3 ${
            isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center px-3">
              <svg className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={t('amazigh_search_placeholder')}
                className={`w-full px-3 py-3 text-sm focus:outline-none ${
                  isDarkMode
                    ? 'bg-gray-800 text-white placeholder-gray-500'
                    : 'bg-white text-gray-900 placeholder-gray-400'
                }`}
              />
              {searchQuery && (
                <button onClick={clearSearch} className={`p-1 rounded-full ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-200'}`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              {searchLoading && (
                <svg className="w-4 h-4 animate-spin text-blue-500 ml-1 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              )}
            </div>
          </div>

          {/* Letter Navigation Bar with scroll arrows */}
          {!isSearchMode && (
            <div className="relative mb-3">
              {/* Left arrow */}
              <button
                onClick={() => letterBarRef.current?.scrollBy({ left: -120, behavior: 'smooth' })}
                className={`lg:hidden absolute left-0 top-0 bottom-1 z-10 w-8 flex items-center justify-center ${
                  isDarkMode ? 'bg-gradient-to-r from-gray-900 via-gray-900/90 to-transparent' : 'bg-gradient-to-r from-gray-50 via-gray-50/90 to-transparent'
                }`}
              >
                <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div
                ref={letterBarRef}
                className="letter-bar flex gap-1 overflow-x-auto pb-1 px-8 lg:px-0"
              >
                {LETTERS.map(letter => {
                  const count = letterCounts[letter] || 0;
                  const isActive = activeLetter === letter;
                  const hasWords = count > 0;
                  return (
                    <button
                      key={letter}
                      data-letter={letter}
                      onClick={() => selectLetter(letter)}
                      disabled={!hasWords}
                      className={`flex-shrink-0 min-w-[34px] h-8 md:min-w-[38px] md:h-9 rounded-lg text-xs md:text-sm font-semibold transition-all flex flex-col items-center justify-center leading-none ${
                        isActive
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                          : hasWords
                            ? isDarkMode
                              ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                            : isDarkMode
                              ? 'bg-gray-800/50 text-gray-700 border border-gray-800 cursor-not-allowed'
                              : 'bg-gray-50 text-gray-300 border border-gray-100 cursor-not-allowed'
                      }`}
                      title={hasWords ? `${letter} (${count})` : `${letter} (0)`}
                    >
                      <span>{letter}</span>
                      {hasWords && (
                        <span className={`text-[8px] leading-none mt-0.5 ${
                          isActive ? 'text-white/70' : isDarkMode ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          {count > 999 ? `${Math.floor(count/1000)}k` : count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Right arrow */}
              <button
                onClick={() => letterBarRef.current?.scrollBy({ left: 120, behavior: 'smooth' })}
                className={`lg:hidden absolute right-0 top-0 bottom-1 z-10 w-8 flex items-center justify-center ${
                  isDarkMode ? 'bg-gradient-to-l from-gray-900 via-gray-900/90 to-transparent' : 'bg-gradient-to-l from-gray-50 via-gray-50/90 to-transparent'
                }`}
              >
                <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* Results Label */}
          <div className={`flex items-center justify-between mb-2 px-1`}>
            <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {isSearchMode
                ? searchLoading
                  ? t('amazigh_searching')
                  : `${searchTotal} ${t('amazigh_results_for')} "${searchQuery}"`
                : loadingWords
                  ? t('amazigh_loading')
                  : `${totalForLetter} ${t('amazigh_words_starting_with')} "${activeLetter}"`
              }
            </p>
          </div>

          {/* Word Cards Grid — show spinner only if no cached words yet.
              If we have words (cached or just-fetched), keep them visible
              while a background refresh runs so the grid never disappears. */}
          <div ref={scrollAreaRef} className="flex-1 min-h-0 pb-2">
            {loadingWords && displayWords.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <svg className="w-8 h-8 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              </div>
            ) : displayWords.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3">
                  {displayWords.map((word, idx) => (
                    <div
                      key={`${word.source_text}-${idx}`}
                      className={`word-card rounded-xl border p-3 md:p-4 ${
                        isDarkMode
                          ? 'bg-gray-800 border-gray-700 hover:border-gray-600 hover:shadow-lg hover:shadow-gray-900/50'
                          : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
                      }`}
                    >
                      {/* English */}
                      <p className={`text-sm md:text-base font-semibold capitalize ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {word.source_text}
                      </p>
                      {/* Tifinagh */}
                      <p className={`text-lg md:text-xl mt-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}
                         style={{ fontFamily: "'Noto Sans Tifinagh', sans-serif" }}>
                        {word.translated_text}
                      </p>
                      {/* Latin */}
                      {word.latin_text && (
                        <p className={`text-xs md:text-sm mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {word.latin_text}
                        </p>
                      )}
                      {/* Actions */}
                      <div className="flex items-center gap-1.5 mt-2">
                        <button
                          onClick={() => copyToClipboard(`${word.translated_text}${word.latin_text ? ' (' + word.latin_text + ')' : ''}`)}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                            isDarkMode
                              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          title={t('amazigh_copy')}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          {t('amazigh_copy')}
                        </button>
                        {word.audio_url && (
                          <button
                            onClick={() => playAudio(word.audio_url)}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                              isDarkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            title={t('amazigh_play_audio')}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                            </svg>
                            {t('amazigh_audio')}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Load More */}
                {!isSearchMode && hasMore && (
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        loadingMore
                          ? isDarkMode ? 'bg-gray-700 text-gray-500 cursor-wait' : 'bg-gray-100 text-gray-400 cursor-wait'
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-md'
                      }`}
                    >
                      {loadingMore ? (
                        <span className="flex items-center gap-2">
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                          {t('amazigh_loading')}
                        </span>
                      ) : (
                        `${t('amazigh_load_more')} (${words.length} / ${totalForLetter})`
                      )}
                    </button>
                  </div>
                )}

                {/* Search: show total if capped */}
                {isSearchMode && searchTotal > PER_PAGE && (
                  <p className={`text-center mt-3 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {t('amazigh_showing_first').replace('{n}', PER_PAGE).replace('{total}', searchTotal)}
                  </p>
                )}

              </>
            ) : (
              /* Empty state */
              !loadingWords && !searchLoading && (
                <div className={`flex flex-col items-center justify-center py-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <span className="text-4xl mb-3">ⵣ</span>
                  <p className="text-sm font-medium">
                    {isSearchMode ? `${t('amazigh_no_results_for')} "${searchQuery}"` : t('amazigh_no_words_found')}
                  </p>
                  {isSearchMode && (
                    <button
                      onClick={clearSearch}
                      className={`mt-2 text-xs font-medium ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                    >
                      {t('amazigh_clear_search')}
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        </div>

      </div>

      {/* Disclaimer Footer — fixed just above mobile nav bar */}
      <div className={`fixed bottom-14 md:bottom-0 left-0 right-0 z-30 px-3 py-1.5 text-center border-t ${
        isDarkMode ? 'bg-gray-900/95 border-gray-700 backdrop-blur-sm' : 'bg-gray-50/95 border-gray-200 backdrop-blur-sm'
      }`}>
        <p className={`text-[8px] md:text-xs leading-relaxed ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {t('amazigh_disclaimer')}
        </p>
      </div>

      {/* Back to Top Button — left side, above disclaimer + nav bar */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={`fixed left-2 bottom-[84px] md:bottom-6 z-40 p-2 rounded-full shadow-lg transition-all hover:scale-110 ${
            isDarkMode
              ? 'bg-gray-700 text-white hover:bg-gray-600 border border-gray-600'
              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
          }`}
          title={t('amazigh_back_to_top')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      )}
    </>
  );
};

export default AmazighTranslation;
